import { useEffect, useState } from 'react';
import type { AssignmentForUser, ItemForUser, SecretPayload } from '@shared/types';
import { KIND_LABEL, LOGIN_METHOD_LABEL, PAYMENT_LABEL, hasOwnPassword, isOverAssigned } from '@shared/types';
import { CYCLE_LABEL, daysUntil, formatMoney, monthlyBaseCents } from '@shared/money';
import { plural } from '@shared/text';
import { useVault } from '../lib/vault';
import { totp } from '../lib/totp';
import { AssignmentEditor } from './AssignmentEditor';
import { ItemIcon } from './ItemIcon';
import { Confirm, CopyButton, ErrorNote, Modal, useAction } from './ui';

function TotpDisplay({ secret }: { secret: string }): JSX.Element {
  const [state, setState] = useState<{ code: string; secondsLeft: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const tick = async (): Promise<void> => {
      try {
        const result = await totp(secret);
        if (active) {
          setState(result);
          setError(null);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : String(err));
      }
    };
    void tick();
    const id = window.setInterval(tick, 1000);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [secret]);

  if (error) return <span className="small" style={{ color: 'var(--danger)' }}>{error}</span>;
  if (!state) return <span className="muted small">…</span>;

  return (
    <div className="secret-row">
      <span className="mono" style={{ fontSize: 18, letterSpacing: 2 }}>
        {state.code.slice(0, 3)} {state.code.slice(3)}
      </span>
      <span className="badge">{state.secondsLeft}s</span>
      <CopyButton value={state.code} />
    </div>
  );
}

function SecretField({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}): JSX.Element | null {
  const [shown, setShown] = useState(false);
  if (!value) return null;
  return (
    <div className="field">
      <label>{label}</label>
      <div className="secret-row">
        {shown ? (
          <span className="mono" style={multiline ? { whiteSpace: 'pre-wrap' } : undefined}>
            {value}
          </span>
        ) : (
          <span className="mono muted">••••••••••••</span>
        )}
        <span className="spacer" />
        <button className="btn-sm" onClick={() => setShown((v) => !v)}>
          {shown ? 'Verbergen' : 'Zeigen'}
        </button>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

/** Eine Zeile in der Platzliste. Das Geheimnis wird erst auf Klick entschlüsselt. */
function SeatRow({
  item,
  assignment,
  canManage,
  onEdit,
  onDelete,
}: {
  item: ItemForUser;
  assignment: AssignmentForUser;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { users, me, readAssignmentSecret } = useVault();
  const [revealed, setRevealed] = useState<SecretPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const holders = users.filter((u) => (assignment.assigneeUids ?? []).includes(u.uid));
  const istMeiner = Boolean(
    me && (assignment.openToAll || (assignment.assigneeUids ?? []).includes(me.uid)),
  );
  const canRead = Boolean(assignment.wrappedKey && assignment.secret);
  const ueberbelegt = isOverAssigned(assignment);
  const isLicense = item.kind === 'license';
  const value = revealed ? (isLicense ? revealed.licenseKey : revealed.password) : '';

  return (
    <div className={assignment.openToAll || holders.length > 0 ? 'seat' : 'seat seat-free'}>
      <div className="seat-main">
        <div className="row-tight">
          <span className="seat-title">{assignment.label || 'Ohne Bezeichnung'}</span>
          {assignment.openToAll ? (
            <span className="badge badge-accent">ganzes Team</span>
          ) : holders.length === 0 ? (
            <span className="badge badge-warn">frei</span>
          ) : assignment.maxUsers !== null ? (
            <span className={ueberbelegt ? 'badge badge-danger' : 'badge'}>
              {holders.length} von {assignment.maxUsers}
            </span>
          ) : null}
          {/* Die Anmeldeart erklaert nebenbei, warum bei manchen Plaetzen gar
              kein Passwort steht. */}
          {assignment.loginMethod && assignment.loginMethod !== 'unknown' ? (
            <span className="badge badge-accent">{LOGIN_METHOD_LABEL[assignment.loginMethod]}</span>
          ) : null}
          {assignment.secret ? (
            <span className="badge">{isLicense ? 'Seriennummer hinterlegt' : 'Passwort hinterlegt'}</span>
          ) : !hasOwnPassword(assignment.loginMethod ?? 'unknown') ? (
            <span className="small muted">kein eigenes Passwort nötig</span>
          ) : null}
        </div>
        <div className="small muted">
          {assignment.openToAll
            ? 'für alle im Team freigegeben'
            : holders.length === 0
              ? 'niemandem zugewiesen'
              : holders.map((u) => (u.uid === me?.uid ? `${u.name} (du)` : u.name)).join(', ')}
          {assignment.device ? ` · ${assignment.device}` : ''}
        </div>
        {ueberbelegt ? (
          <div className="small" style={{ color: 'var(--danger)', marginTop: 4 }}>
            Überbelegt – die Lizenz erlaubt nur {assignment.maxUsers}.
          </div>
        ) : null}
        {error ? <div className="small" style={{ color: 'var(--danger)' }}>{error}</div> : null}
        {revealed ? (
          <div className="secret-row" style={{ marginTop: 8 }}>
            <span className="mono">{value || '(leer)'}</span>
            <span className="spacer" />
            <CopyButton value={value} />
            <button className="btn-sm" onClick={() => setRevealed(null)}>
              Verbergen
            </button>
          </div>
        ) : null}
        {revealed?.notes ? <div className="small muted" style={{ marginTop: 6 }}>{revealed.notes}</div> : null}
      </div>

      <div className="row-tight">
        {canRead && !revealed ? (
          <button
            className="btn-sm"
            onClick={async () => {
              try {
                setRevealed(await readAssignmentSecret(item, assignment));
                setError(null);
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              }
            }}
          >
            Zeigen
          </button>
        ) : null}
        {/* Den eigenen Zugang pflegt jeder selbst - bei einem persönlichen
            Konto legt ohnehin niemand sonst das Passwort fest. */}
        {!canManage && istMeiner ? (
          <button className="btn-sm" onClick={onEdit}>
            {assignment.secret
              ? 'Passwort ändern'
              : hasOwnPassword(assignment.loginMethod ?? 'unknown')
                ? 'Passwort hinterlegen'
                : 'Bearbeiten'}
          </button>
        ) : null}
        {canManage ? (
          <>
            <button className="btn-quiet btn-sm" onClick={onEdit}>
              Bearbeiten
            </button>
            <button className="btn-quiet btn-sm" onClick={onDelete}>
              Entfernen
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function ItemDetail({
  item,
  onEdit,
  onClose,
}: {
  item: ItemForUser;
  onEdit: () => void;
  onClose: () => void;
}): JSX.Element {
  const { users, me, isAdmin, readSecret, shareItem, revokeAccess, deleteItem, removeAssignment } =
    useVault();
  const [secret, setSecret] = useState<SecretPayload | null>(null);
  const [shareUid, setShareUid] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [seatEditor, setSeatEditor] = useState<{ assignment: AssignmentForUser | null } | null>(null);
  const [seatToRemove, setSeatToRemove] = useState<AssignmentForUser | null>(null);
  const { busy, error, setError, run } = useAction();

  const hasAccess = Boolean(item.wrappedKey);
  const canManageSeats =
    isAdmin || item.ownerUid === me?.uid || (me ? item.accessUids.includes(me.uid) : false);

  /** Plätze, die dieser Person gehören - sie darf dort ihr Passwort setzen. */
  const meinePlaetze = item.assignments.filter(
    (a) => me && (a.openToAll || (a.assigneeUids ?? []).includes(me.uid)),
  );
  /**
   * Ob diese Person den Eintrag verwaltet oder ihn nur benutzt. Davon haengt
   * ab, ob Vertragsdaten, Freigabelisten und Kritikalitaet ueberhaupt
   * auftauchen - fuer die reine Nutzung ist das alles Beiwerk.
   */
  const istVerwaltung = canManageSeats;

  useEffect(() => {
    if (!hasAccess) return;
    let cancelled = false;
    readSecret(item)
      .then((payload) => {
        if (!cancelled) setSecret(payload);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [item, hasAccess, readSecret, setError]);

  const grantees = users.filter((u) => item.accessUids.includes(u.uid));
  const candidates = users.filter(
    (u) => !item.accessUids.includes(u.uid) && u.status === 'active' && u.publicKey,
  );
  const owner = users.find((u) => u.uid === item.ownerUid);
  const renewal = daysUntil(item.renewalDate);
  const deadline = daysUntil(item.cancelBeforeDate);

  const taken = item.assignments.filter((a) => a.openToAll || (a.assigneeUids ?? []).length > 0).length;
  const paidSeats = item.seats ?? 0;
  const spareSeats = paidSeats > 0 ? paidSeats - taken : 0;
  const isShared = item.kind === 'shared';
  const monthly = monthlyBaseCents(item);

  return (
    <>
      <Modal
        wide
        title={item.title}
        subtitle={[KIND_LABEL[item.kind], item.category].filter(Boolean).join(' · ')}
        onClose={onClose}
        footer={
          <>
            {hasAccess || isAdmin ? (
              <button className="btn-danger" onClick={() => setConfirmDelete(true)}>
                Löschen
              </button>
            ) : null}
            <span className="spacer" />
            <button onClick={onClose}>Schließen</button>
            <button className="btn-primary" onClick={onEdit}>
              Bearbeiten
            </button>
          </>
        }
      >
        <ErrorNote error={error} />

        <div className="row" style={{ marginBottom: 14 }}>
          <span
            className={
              item.status === 'cancelled'
                ? 'badge'
                : item.status === 'trial'
                  ? 'badge badge-warn'
                  : 'badge badge-ok'
            }
          >
            {item.status === 'active' ? 'Aktiv' : item.status === 'trial' ? 'Testphase' : 'Gekündigt'}
          </span>
          {isAdmin && !item.hasCost ? <span className="badge">kostenlos</span> : null}
          {/* Die Kritikalität steuert Warnungen in der Verwaltung. Wer den
              Zugang nur benutzt, kann damit nichts anfangen. */}
          {isAdmin ? (
            <span className={item.criticality === 'high' ? 'badge badge-danger' : 'badge'}>
              Kritikalität {item.criticality === 'high' ? 'hoch' : item.criticality === 'medium' ? 'mittel' : 'niedrig'}
            </span>
          ) : null}
          {item.needsRotation ? <span className="badge badge-danger">Passwort ändern</span> : null}
          {item.url ? (
            <a href={item.url} target="_blank" rel="noreferrer noopener" className="small">
              {item.url.replace(/^https?:\/\//, '')}{'\u00a0↗'}
            </a>
          ) : null}
          {isAdmin && item.invoiceUrl ? (
            <a href={item.invoiceUrl} target="_blank" rel="noreferrer noopener" className="small">
              Rechnungen{'\u00a0↗'}
            </a>
          ) : null}
          {/* Die Anleitung sehen alle - sie ist ja gerade fuer die da, die den
              Zugang benutzen sollen. */}
          {item.guideUrl ? (
            <a href={item.guideUrl} target="_blank" rel="noreferrer noopener" className="small">
              {item.guideTitle || 'Anleitung'}{'\u00a0↗'}
            </a>
          ) : null}
        </div>

        {item.needsRotation ? (
          <div className="note note-danger">
            <strong>Dieses Passwort muss beim Anbieter geändert werden.</strong>
            <div className="small" style={{ marginTop: 4 }}>
              Grund: {item.rotationReason ?? 'Zugriff wurde entzogen'}. Erst wenn das neue Passwort
              hier gespeichert ist, gilt der Eintrag als erledigt.
            </div>
          </div>
        ) : null}

        {/* Früher stand hier eine rote Warnung. Dass ein Zugang jemand anderem
            gehört, ist aber der Normalfall und kein Fehler. */}
        {!hasAccess ? (
          <div className="small muted">
            {meinePlaetze.length > 0
              ? 'Für den gemeinsamen Zugang bist du nicht freigeschaltet – dein persönlicher Platz steht unten.'
              : 'Für diesen Zugang bist du nicht freigeschaltet. Sag Bescheid, wenn du ihn brauchst.'}
          </div>
        ) : null}

        {hasAccess && secret ? (
          <>
            <h3 style={{ margin: '18px 0 10px' }}>
              {isShared ? 'Gemeinsame Zugangsdaten' : 'Verwaltungszugang'}
            </h3>
            {item.username ? (
              <div className="field">
                <label>Login-Name</label>
                <div className="secret-row">
                  <span className="mono">{item.username}</span>
                  <span className="spacer" />
                  <CopyButton value={item.username} />
                </div>
              </div>
            ) : null}
            <SecretField label="Passwort" value={secret.password} />
            <SecretField label="Haupt-Seriennummer" value={secret.licenseKey} />
            {secret.totpSecret ? (
              <div className="field">
                <label>Zwei-Faktor-Code</label>
                <TotpDisplay secret={secret.totpSecret} />
              </div>
            ) : null}
            <SecretField label="Wiederherstellungscodes" value={secret.recoveryCodes} multiline />
            <SecretField label="Notizen" value={secret.notes} multiline />
          </>
        ) : null}

        {!isShared ? (
          <>
            <div className="card-title" style={{ margin: '22px 0 10px' }}>
              <h3>
                {item.kind === 'license' ? 'Lizenzen' : 'Plätze'}
                {paidSeats > 0 ? ` – ${taken} von ${paidSeats} vergeben` : ` – ${taken} vergeben`}
              </h3>
              {canManageSeats ? (
                <button
                  className="btn-sm"
                  onClick={() => setSeatEditor({ assignment: null })}
                >
                  Hinzufügen
                </button>
              ) : null}
            </div>

            {isAdmin && spareSeats > 0 && item.hasCost ? (
              <div className="note note-warn" style={{ marginBottom: 10 }}>
                Ihr bezahlt {plural(spareSeats, 'Platz', 'Plätze')}, {spareSeats === 1 ? 'der' : 'die'}{' '}
                niemand nutzt
                {monthly > 0 && paidSeats > 0
                  ? ` – etwa ${formatMoney(Math.round((monthly / paidSeats) * spareSeats))} im Monat.`
                  : '.'}
              </div>
            ) : null}

            {item.assignments.length === 0 ? (
              <div className="empty">
                Noch nichts eingetragen.
                {canManageSeats
                  ? ` Leg für jede Person ${item.kind === 'license' ? 'eine Lizenz' : 'einen Platz'} an.`
                  : ''}
              </div>
            ) : (
              <div className="stack">
                {item.assignments.map((assignment) => (
                  <SeatRow
                    key={assignment.id}
                    item={item}
                    assignment={assignment}
                    canManage={canManageSeats}
                    onEdit={() => setSeatEditor({ assignment })}
                    onDelete={() => setSeatToRemove(assignment)}
                  />
                ))}
              </div>
            )}
          </>
        ) : null}

        {/* Vertragsdaten interessieren, wer den Vertrag verantwortet. Für alle
            anderen bleibt der Abschnitt weg statt mit Platzhaltern zu füllen. */}
        {istVerwaltung ? (
          <>
        <h3 style={{ margin: '22px 0 10px' }}>Vertrag</h3>
        <table>
          <tbody>
            {!isAdmin ? (
              <tr>
                <td className="muted small">Kosten</td>
                <td className="muted">Preise siehst du nicht.</td>
              </tr>
            ) : !item.hasCost ? (
              <tr>
                <td className="muted small">Kosten</td>
                <td>Keine – reiner Zugang ohne Vertrag.</td>
              </tr>
            ) : (
              <tr>
                <td className="muted small">Preis</td>
                <td>
                  <span className="mono">{formatMoney(item.priceCents, item.currency)}</span>{' '}
                  {CYCLE_LABEL[item.cycle]}
                  {item.priceIsPerSeat ? ` je Platz × ${item.seats ?? 1}` : ''}
                  {/* Bei monatlicher Abrechnung stünde hier zweimal dasselbe. */}
                  {item.cycle !== 'onetime' && item.cycle !== 'monthly' ? (
                    <span className="muted small"> · {formatMoney(monthly)} umgelegt pro Monat</span>
                  ) : null}
                </td>
              </tr>
            )}
            {isAdmin && item.hasCost && item.paymentMethod !== 'unknown' ? (
              <tr>
                <td className="muted small">Zahlungsmittel</td>
                <td>
                  {PAYMENT_LABEL[item.paymentMethod]}
                  {item.paymentDetail ? <span className="muted"> · {item.paymentDetail}</span> : null}
                </td>
              </tr>
            ) : null}
            {isAdmin && item.billingDay ? (
              <tr>
                <td className="muted small">Abbuchung</td>
                <td>am {item.billingDay}. jedes Monats</td>
              </tr>
            ) : null}
            {isAdmin && item.purchaseDate ? (
              <tr>
                <td className="muted small">Gekauft am</td>
                <td>{item.purchaseDate}</td>
              </tr>
            ) : null}
            <tr>
              <td className="muted small">Verantwortlich</td>
              <td>{owner ? owner.name : <span className="muted">niemand zugeordnet</span>}</td>
            </tr>
            {item.renewalDate ? (
              <tr>
                <td className="muted small">Verlängerung</td>
                <td>
                  {item.renewalDate}
                  {renewal !== null ? (
                    <span className="muted small">
                      {' '}
                      · {renewal < 0 ? `${Math.abs(renewal)} Tage überfällig` : `in ${renewal} Tagen`}
                    </span>
                  ) : null}
                </td>
              </tr>
            ) : null}
            {item.cancelBeforeDate ? (
              <tr>
                <td className="muted small">Kündigen bis</td>
                <td>
                  {item.cancelBeforeDate}
                  {deadline !== null ? (
                    <span
                      className={deadline <= 14 ? 'badge badge-warn' : 'muted small'}
                      style={{ marginLeft: 6 }}
                    >
                      {deadline < 0 ? 'Frist verstrichen' : `noch ${deadline} Tage`}
                    </span>
                  ) : null}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className="muted small">Zuletzt geändert</td>
              <td className="small muted">
                {new Date(item.updatedAt).toLocaleString('de-DE')}
                {item.lastRotatedAt
                  ? ` · Passwort zuletzt am ${new Date(item.lastRotatedAt).toLocaleDateString('de-DE')}`
                  : ''}
              </td>
            </tr>
          </tbody>
        </table>
          </>
        ) : null}

        {/* Wer nur mitliest, braucht keine Liste aller Freigaben. */}
        {istVerwaltung ? (
          <>
        <h3 style={{ margin: '22px 0 10px' }}>
          {isShared ? `Zugriff (${grantees.length})` : `Verwaltung (${grantees.length})`}
        </h3>
        <div className="stack">
          {grantees.map((u) => (
            <div key={u.uid} className="row">
              <span>{u.name}</span>
              <span className="muted small">{u.email}</span>
              {u.uid === me?.uid ? <span className="badge">du</span> : null}
              {u.status === 'disabled' ? <span className="badge badge-danger">deaktiviert</span> : null}
              <span className="spacer" />
              {(hasAccess || isAdmin) && grantees.length > 1 ? (
                <button
                  className="btn-quiet btn-sm"
                  disabled={busy}
                  onClick={() => void run(() => revokeAccess(item, u.uid))}
                >
                  Entziehen
                </button>
              ) : null}
            </div>
          ))}
        </div>

        {hasAccess && candidates.length > 0 ? (
          <div className="row" style={{ marginTop: 12 }}>
            <select
              value={shareUid}
              onChange={(e) => setShareUid(e.target.value)}
              style={{ maxWidth: 260 }}
            >
              <option value="">Person auswählen …</option>
              {candidates.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
            <button
              disabled={!shareUid || busy}
              onClick={async () => {
                const ok = await run(() => shareItem(item, [shareUid]));
                if (ok !== undefined) setShareUid('');
              }}
            >
              Freigeben
            </button>
          </div>
        ) : null}
          </>
        ) : null}
      </Modal>

      {seatEditor ? (
        <AssignmentEditor
          item={item}
          assignment={seatEditor.assignment}
          secretOnly={!canManageSeats}
          onClose={() => setSeatEditor(null)}
        />
      ) : null}

      {seatToRemove ? (
        <Confirm
          danger
          title="Platz entfernen?"
          confirmLabel="Entfernen"
          message={
            <p>
              „{seatToRemove.label || 'Platz'}" wird aus LOCKER gelöscht – samt hinterlegtem
              Geheimnis und allen Zuweisungen. Beim Anbieter selbst ändert sich dadurch nichts.
            </p>
          }
          onConfirm={async () => {
            await removeAssignment(item, seatToRemove.id);
          }}
          onClose={() => setSeatToRemove(null)}
        />
      ) : null}

      {confirmDelete ? (
        <Confirm
          danger
          title="Eintrag löschen?"
          confirmLabel="Endgültig löschen"
          message={
            <>
              <p>
                „{item.title}" wird mitsamt Passwort, Notizen
                {item.assignments.length > 0 ? ' und allen Plätzen' : ''} gelöscht. Das lässt sich
                nicht rückgängig machen.
              </p>
              <p className="muted">
                Der Vertrag beim Anbieter läuft davon unberührt weiter – kündigen musst du dort
                separat.
              </p>
            </>
          }
          onConfirm={async () => {
            await deleteItem(item.id);
            onClose();
          }}
          onClose={() => setConfirmDelete(false)}
        />
      ) : null}
    </>
  );
}
